import { absoluteHostUrl, isPreviewHost } from '../../constants/landingHosts';
import { EXAM_HOSTS_COPY as COPY } from '../../constants/examHosts_messages.fr';
import { DIPLOMAS } from './examChamberConstants';
import { buildExamTags } from './utils/examSeo';

/**
 * Exam hosts (owner ruling 2026-11-01). Only each host's entry pages live here;
 * deeper `/examens/…` paths on these hosts reuse the www chamber routes and keep
 * their www canonical. www `/examens` is unchanged.
 */
export const EXAM_HOST = Object.freeze({ ANNALES: 'annales.neoprof.fr', BAC: 'bac.neoprof.fr', BREVET: 'brevet.neoprof.fr' });
export const EXAM_HOST_KIND = Object.freeze({ CATALOGUE: 'catalogue', JOURNEY: 'journey' });
/** Local/preview alias: `/h/<sub><route>` (e.g. `/h/annales/bac/premiere`). */
export const EXAM_HOST_PREVIEW_PREFIX = '/h';

const ALL = Object.freeze(DIPLOMAS.map((diploma) => diploma.slug));
const route = (diplomas) => Object.freeze({ diplomas: Object.freeze(diplomas) });

export const EXAM_HOSTS = Object.freeze({
  [EXAM_HOST.ANNALES]: Object.freeze({
    kind: EXAM_HOST_KIND.CATALOGUE,
    routes: Object.freeze({
      '/': route(ALL), '/brevet': route(['brevet']),
      '/bac/premiere': route(['bac-premiere']), '/bac/terminale': route(['bac-terminale']),
    }),
  }),
  // Brevet: one diploma, straight into its chamber. Bac: the level is asked first.
  [EXAM_HOST.BREVET]: Object.freeze({ kind: EXAM_HOST_KIND.JOURNEY, routes: Object.freeze({ '/': route(['brevet']) }) }),
  [EXAM_HOST.BAC]: Object.freeze({ kind: EXAM_HOST_KIND.JOURNEY, routes: Object.freeze({ '/': route(['bac-premiere', 'bac-terminale']) }) }),
});

const normalize = (pathname) => (String(pathname || '/').split(/[?#]/)[0].replace(/\/+$/, '') || '/');
const subdomain = (host) => host.split('.')[0];

export const isExamHost = (hostname) => Object.prototype.hasOwnProperty.call(EXAM_HOSTS, hostname);

function entryFor(host, path) {
  const config = EXAM_HOSTS[host];
  const entry = config?.routes[path];
  return entry ? { host, route: path, kind: config.kind, diplomas: entry.diplomas } : null;
}

/** The host entry page for this location, or null (deeper paths fall through to the chamber). */
export function resolveExamHostRoute({ hostname = '', pathname = '/' } = {}) {
  const host = String(hostname).toLowerCase().replace(/\.$/, '');
  const path = normalize(pathname);
  if (isExamHost(host)) return entryFor(host, path);
  if (!isPreviewHost(host) || !path.startsWith(`${EXAM_HOST_PREVIEW_PREFIX}/`)) return null;
  const [, , sub, ...rest] = path.split('/');
  const previewHost = Object.keys(EXAM_HOSTS).find((candidate) => subdomain(candidate) === sub);
  const entry = previewHost ? entryFor(previewHost, `/${rest.join('/')}`.replace(/\/+$/, '') || '/') : null;
  return entry ? { ...entry, preview: true } : null;
}

/** Per-host prerendered routes, `{host, route, indexed}` (same shape as the Formations routes). */
export const examHostRoutes = () => Object.entries(EXAM_HOSTS).flatMap(([host, config]) => (
  Object.keys(config.routes).map((path) => ({ host, route: path, indexed: true }))
));

/** Head of one host entry page: its own canonical; previews are never indexed. */
export function buildExamHostMeta({ host, route: path, preview = false }) {
  const copy = COPY.PAGES[host][path];
  return {
    title: copy.title, description: copy.description, canonical: absoluteHostUrl(host, path),
    heading: copy.heading, lead: copy.lead, lang: 'fr', jsonLd: [],
    ...(preview ? { robots: 'noindex,follow' } : {}),
  };
}

const ANNALES_ROUTE = Object.freeze({ brevet: '/brevet', 'bac-premiere': '/bac/premiere', 'bac-terminale': '/bac/terminale' });
const JOURNEY_HOST = Object.freeze({ brevet: EXAM_HOST.BREVET, 'bac-premiere': EXAM_HOST.BAC, 'bac-terminale': EXAM_HOST.BAC });
const hostHref = (host, path, hostname) => (isPreviewHost(hostname)
  ? `${EXAM_HOST_PREVIEW_PREFIX}/${subdomain(host)}${path === '/' ? '' : path}` : absoluteHostUrl(host, path));

/** The annales page of a diploma (all diplomas when unknown); `/h/annales…` on preview hosts. */
export const annalesHref = (diploma = '', hostname = window.location.hostname) => (
  hostHref(EXAM_HOST.ANNALES, ANNALES_ROUTE[diploma] || '/', hostname)
);
/** The journey host of a diploma (brevet. or bac.). */
export const journeyHref = (diploma, hostname = window.location.hostname) => (
  hostHref(JOURNEY_HOST[diploma] || EXAM_HOST.BREVET, '/', hostname)
);

export const listExamHostPages = () => examHostRoutes().map(({ host, route: path }) => {
  const meta = buildExamHostMeta({ host, route: path });
  return { host, route: path, meta, tags: buildExamTags(meta) };
});
